import { Link, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useApp } from '@/store';
import { useT, BILLING_LABEL_KEYS } from '@/lib/i18n';
import { Billing } from '@/data/types';
import { fmtDate } from '@/lib/utils';
import { Card, CardContent } from '@/components/ui/card';
import { PhotoUploader } from '@/components/PhotoUploader';

// Per-device inspection history: every visit's reading for one device, newest
// first — so the office can see when a refill / spare part was last done.
export function DeviceHistory() {
  const t = useT();
  const { id, deviceId } = useParams();
  const customer = useApp(s => s.customers.find(c => c.id === id));
  const inspections = useApp(s => s.inspections);
  const spareParts = useApp(s => s.spareParts);

  if (!customer) return <div>{t('common.notFound')}</div>;

  const device =
    customer.extinguishers.find(e => e.id === deviceId) ??
    customer.hoses.find(h => h.id === deviceId);
  const number =
    (device && 'deviceNumber' in device && device.deviceNumber) || deviceId;
  const partName = (a: string) =>
    spareParts.find(p => p.articleNr === a)?.name ?? a;

  const rows = inspections
    .filter(i => i.customerId === customer.id)
    .map(i => ({ i, r: (i.readings ?? []).find(x => x.deviceId === deviceId) }))
    .filter(x => x.r)
    .sort((a, b) => (a.i.date < b.i.date ? 1 : -1));

  return (
    <div className="space-y-4">
      <Link
        to={`/customer/${customer.id}`}
        className="text-sm text-muted-foreground hover:text-primary inline-flex items-center gap-1"
      >
        <ArrowLeft className="h-3 w-3" /> {t('common.back')}
      </Link>
      <h1 className="text-2xl font-bold tracking-tight">
        <span className="font-mono text-destructive">#{number}</span>{' '}
        <span className="text-muted-foreground text-base">
          {device?.location}
        </span>
      </h1>

      <div className="space-y-2">
        {rows.length === 0 && (
          <p className="text-sm text-muted-foreground">
            {t('customer.noInspections')}
          </p>
        )}
        {rows.map(({ i, r }) => (
          <Card key={i.id}>
            <CardContent className="pt-4 text-sm space-y-1">
              <div className="flex justify-between">
                <span className="font-semibold">{fmtDate(i.date)}</span>
                <span className="text-xs text-muted-foreground">
                  {i.technician}
                </span>
              </div>
              {r?.kind === 'extinguisher' && (
                <div className="text-xs text-muted-foreground space-y-0.5">
                  <div>
                    {t('inspection.condition')}: {r.extCondition} ·{' '}
                    {t('ext.billing')}:{' '}
                    {t(BILLING_LABEL_KEYS[r.billing ?? Billing.NONE])}
                  </div>
                  <div>
                    {t('inspection.refilled')}: {fmtDate(r.refilled) || '—'}
                  </div>
                  <div>
                    {t('ext.spareParts')}:{' '}
                    {(r.spareParts ?? []).map(partName).join(', ') || '—'}
                  </div>
                  {r.note && (
                    <div>
                      {t('inspection.note')}: {r.note}
                    </div>
                  )}
                </div>
              )}
              {r?.kind === 'hose' && (
                <div className="text-xs text-muted-foreground space-y-0.5">
                  <div>
                    {t('hose.restingPressure')}: {r.restingPressure} ·{' '}
                    {t('hose.flowPressure')}: {r.flowPressure} ·{' '}
                    {t('hose.flowRate')}: {r.flowRate}
                  </div>
                  {r.condition && (
                    <div>
                      {Object.entries(r.condition)
                        .map(([k, v]) => `${k}: ${v}`)
                        .join(' · ')}
                    </div>
                  )}
                </div>
              )}
              {r?.photos && r.photos.length > 0 && (
                <div className="pt-1">
                  <div className="mb-1 text-xs text-muted-foreground">
                    {t('photos.title')}
                  </div>
                  <PhotoUploader
                    value={r.photos}
                    onChange={() => {}}
                    disabled
                  />
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
